// HsnDetail.jsx
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Box, Button, Checkbox, TextField, IconButton, Typography, Paper, Stack, Grid, Avatar,} from '@mui/material';
import { Add, Delete, FilterList } from '@mui/icons-material';
import { FaFileExcel } from 'react-icons/fa';
import { Link, useNavigate } from 'react-router-dom';
import DetailCard from '../components/DetailCard'; // Adjust the path as per your project structure
import LoadingOverlay from '../components/LoadingOverlay';

// const path = `http://103.251.218.254:91/api/api`

const HsnDetail = ({ loggedInUser, companyData, connectionString }) => {
    const navigate = useNavigate();
    const [data, setData] = useState([]);
    const [HSNNoSearchTerm, setHSNNoSearchTerm] = useState('');
    const [resetFlag, setResetFlag] = useState(0);
    const [totalCount, setTotalCount] = useState(0);
    const [offset, setOffset] = useState(0);
    const [limit] = useState(5);
    const [selectAll, setSelectAll] = useState(false);
    const [selectedRows, setSelectedRows] = useState([]);
    const [mode, setMode] = useState('search');
    const [isVisible, setIsVisible] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const path = `${connectionString}/api`;

    useEffect(() => {
        fetchData();
        fetchTotalCount();
    }, [resetFlag, offset, limit, mode]);

    const fetchData = async () => {
        try {
            setIsLoading(true);
            let apiUrl = `${path}/hsn/search?mode=${mode}&offset=${offset}&limit=${limit}`;
            if (mode === 'search') {
                if (resetFlag === 1) {
                    setHSNNoSearchTerm('');
                    setOffset(0);
                    setResetFlag(0);
                    setSelectedRows([]);
                    setSelectAll(false);
                } else {
                    apiUrl += `&HSNNo=${HSNNoSearchTerm}`;
                }

                const response = await axios.get(apiUrl);
                if (Array.isArray(response.data)) {
                    setData(offset === 0 ? response.data : [...data, ...response.data]);
                }
            }
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchTotalCount = async () => {
        try {
            const response = await axios.get(`${path}/hsn/count`);
            setTotalCount(response.data);
        } catch (error) {
            console.error('Error fetching total count:', error);
        }
    };

    const funExportData = () => {
        axios({
            url: `${path}/hsn/search?mode=export`,
            method: 'GET',
            responseType: 'blob',
        }).then((response) => {
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'exportedFileHSN.xlsx');
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            setMode('search');
        }).catch((error) => {
            console.error("Excel download error!", error);
        });
    };

    const funSearch = () => {
        setOffset(0);
        setMode('search');
        fetchData();
        fetchTotalCount();
    };

    const funReset = () => {
        setResetFlag(1);
        setMode('search');
    };

    const funShowMore = () => {
        setOffset(prev => prev + limit);
    };

    const funSelectAll = () => {
        setSelectAll(!selectAll);
        setSelectedRows(!selectAll ? data.map(item => item.HSNId) : []);
    };

    const funRowSelect = (id) => {
        setSelectedRows(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
    };

    const funDeleteSelectedRows = async () => {
        try {
            await axios.post(`${path}/hsn/Delete`, { objAryId: selectedRows });
            funReset();
        } catch (error) {
            console.error('Delete error:', error);
        }
    };

    const handleCancel = () => {
        setIsVisible(false);
        funReset();
    };

    const handleFilter = () => {
        setIsVisible(false);
        funSearch();
    };

    return (
        <>
            <LoadingOverlay isLoading={isLoading} message="Loading data..." />
            <Box p={2} display="flex" flexDirection="column" gap={2}>
            {/* Top Bar */}
            <Paper sx={{ p: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box display="flex" alignItems="center" gap={2}>
                    <IconButton color="primary" onClick={() => setIsVisible(p => !p)}><FilterList /></IconButton>
                    <Stack direction="row" spacing={1}>
                        <IconButton component={Link} to="/main/hsnMaster" color="primary"><Add /></IconButton>
                        <Checkbox checked={selectAll} onChange={funSelectAll} />
                        <IconButton color="error" onClick={funDeleteSelectedRows} disabled={selectedRows.length === 0}><Delete /></IconButton>
                        <IconButton onClick={funExportData} color="success"><FaFileExcel size={22} /></IconButton>
                    </Stack>
                </Box>
                <Typography variant="body2" color="text.secondary">{data.length}/{totalCount}</Typography>
            </Paper>

            {/* Filter Section */}
            {isVisible && (
                <Box display="flex" justifyContent="center">
                    <Paper
                        elevation={1}
                        sx={{
                            p: 4,
                            borderRadius: 3,
                            bgcolor: '#f1f5f9',
                            border: '1px solid #e5e7eb',
                            backdropFilter: 'blur(2px)',
                        }}
                    >
                        <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: '#1f2937', fontSize: '1.125rem' }}>
                            Filter HSN Records
                        </Typography>

                        <Grid container spacing={3}>
                            <Grid item xs={12} sm={6} md={4}>
                                <TextField fullWidth label="HSN No." variant="outlined" size="small" value={HSNNoSearchTerm} onChange={(e) => setHSNNoSearchTerm(e.target.value)} InputLabelProps={{ sx: { color: '#6b7280', fontSize: '0.875rem' }, }} inputProps={{ style: { fontSize: '0.875rem' } }} />
                            </Grid>

                            <Grid item xs={12}>
                                <Stack direction="row" spacing={2} justifyContent="center">
                                    <Button variant="contained" onClick={handleFilter} disableElevation sx={{ textTransform: 'none', fontWeight: 500, borderRadius: 2, bgcolor: '#1877F2', '&:hover': { bgcolor: '#166fe5' }, fontSize: '0.875rem', px: 4, }}>
                                        Search
                                    </Button>
                                    <Button variant="outlined" onClick={handleCancel} sx={{ textTransform: 'none', fontWeight: 500, borderRadius: 2, borderColor: '#DC2626', color: '#DC2626', fontSize: '0.875rem', px: 4, '&:hover': { borderColor: '#b91c1c', color: '#b91c1c', backgroundColor: '#fef2f2' }, }}>
                                        Cancel
                                    </Button>
                                </Stack>
                            </Grid>
                        </Grid>
                    </Paper>
                </Box>
            )}

            {/* Cards */}
            {data.map((item, index) => (
                <DetailCard
                    key={item.HSNId}
                    id={item.HSNId}
                    title={item.HSNNo}
                    avatarText={item.HSNDescription?.charAt(0)}
                    subtitleList={[ item.HSNDescription || 'No description available' ]}
                    selected={selectedRows.includes(item.HSNId)}
                    onSelect={funRowSelect}
                    onEdit={(id) => navigate(`/main/editHsnMaster/${id}`)}
                    backgroundColor="#f1f5f9"
                />
            ))}

            {/* Show More Button */}
            {data.length < totalCount && (
                <Box textAlign="center" mt={1}>
                    <Button variant="outlined" onClick={funShowMore}>Show More</Button>
                </Box>
            )}
        </Box>
        </>
    );
};

export default HsnDetail;
